import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import {
  BooleanTypeSelectItemsProviderService,
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
} from '@web/shared/ui';
import { Observable } from 'rxjs';
import { once } from '@web/utils';
import {
  InvoiceKsefStatusProviderService,
  InvoiceStatusProviderService,
  InvoiceTypeProviderService,
} from '@web/shared/module/shop';
import { AdminInvoiceListCustomCellTemplateComponent } from '../../components/admin-invoice-list-custom-cell-template/admin-invoice-list-custom-cell-template.component';
import { AdminInvoiceCollectionItem } from '../../models/admin-invoice-collection-item';
import { AdminInvoiceCollectionFilter } from '../../models/admin-invoice-collection-filter';
import { AdminInvoiceListItemAction } from '../../enums/admin-invoice-list-item-action';
import { AdminInvoiceDataSourceService } from '../../services/admin-invoice-data-source.service';

@Component({
  selector: 'app-admin-invoice-list',
  imports: [TableComponent, AdminInvoiceListCustomCellTemplateComponent],
  templateUrl: './admin-invoice-list.component.html',
  styleUrl: './admin-invoice-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminInvoiceListComponent implements OnInit {
  protected config!: TableConfig<
    AdminInvoiceCollectionItem,
    AdminInvoiceCollectionFilter,
    AdminInvoiceListItemAction
  >;

  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminInvoiceCollectionItem, AdminInvoiceCollectionFilter, AdminInvoiceListItemAction>).create();
  private readonly invoiceDataSource = inject(AdminInvoiceDataSourceService);
  private readonly statusProvider = inject(InvoiceStatusProviderService);
  /* prettier-ignore */
  private readonly ksefStatusProvider = inject(InvoiceKsefStatusProviderService);
  private readonly typeProvider = inject(InvoiceTypeProviderService);
  private readonly table = inject(TableService);

  /* prettier-ignore */
  private readonly booleanTypeSelectItemsProvider = inject(BooleanTypeSelectItemsProviderService);

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .itemActions((builder) => [
        builder
          .initCommand(AdminInvoiceListItemAction.Synchronize)
          .icon(Icon.Recycle)
          .tooltip('Synchronizuj')
          .visibility((item) => item.canBeSynchronized)
          .command((item) => this.synchronize(item))
          .build(),

        builder
          .initCommand(AdminInvoiceListItemAction.SendToKsef)
          .icon(Icon.SendArrowDown)
          .tooltip('Wyślij do KSeF')
          .visibility((item) => item.canBeSendToKsef)
          .command((item) => this.sendToKsef(item))
          .build(),

        builder
          .initCommand(AdminInvoiceListItemAction.Download)
          .icon(Icon.CloudDownloadFill)
          .tooltip('Pobierz')
          .visibility((item) => item.canBeDownloaded)
          .command((item) => this.download(item))
          .build(),
      ])
      .filters((builder) => [
        builder.initText('number').build(),

        builder
          .initSelect('hasError')
          .dictionary(() => this.booleanTypeSelectItemsProvider.selectItems())
          .build(),

        builder
          .initMultiSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),

        builder
          .initMultiSelect('ksefStatus')
          .dictionary(() => this.ksefStatusProvider.selectItems())
          .build(),

        builder
          .initMultiSelect('type')
          .dictionary(() => this.typeProvider.selectItems())
          .build(),

        builder.initText('taskReferenceNumber').build(),
        builder.initText('ksefNumber').build(),
        builder.initDateRange('createdAt').build(),
      ])
      .columns((builder) => [
        builder
          .initText('number')
          .label('Numer')
          .width('12rem')
          .sortKey('number')
          .filterKey('number')
          .build(),

        builder
          .initCustom('hasError')
          .width('10rem')
          .label('Błąd faktury')
          .sortKey('hasError')
          .filterKey('hasError')
          .build(),

        builder
          .initCustom('status')
          .width('14rem')
          .label('Status')
          .sortKey('status')
          .filterKey('status')
          .build(),

        builder
          .initCustom('type')
          .width('14rem')
          .label('Rodzaj')
          .sortKey('type')
          .filterKey('type')
          .build(),

        builder
          .initText('taskReferenceNumber')
          .label('Zewnętrzny numer referencyjny')
          .width('22rem')
          .sortKey('taskReferenceNumber')
          .filterKey('taskReferenceNumber')
          .build(),

        builder
          .initCustom('ksefStatus')
          .width('14rem')
          .label('Status KSeF')
          .sortKey('ksefStatus')
          .filterKey('ksefStatus')
          .build(),

        builder
          .initText('ksefNumber')
          .label('Numer KSeF')
          .width('22rem')
          .sortKey('ksefNumber')
          .filterKey('ksefNumber')
          .build(),

        builder
          .initDateTime('createdAt')
          .label('Data utworzenia')
          .sortKey('createdAt')
          .filterKey('createdAt')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminInvoiceCollectionFilter>,
        ): Observable<TableDataPayload<AdminInvoiceCollectionItem>> =>
          this.invoiceDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private synchronize(item: AdminInvoiceCollectionItem): void {
    this.invoiceDataSource
      .confirmAndSynchronizeInvoiceById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private sendToKsef(item: AdminInvoiceCollectionItem): void {
    this.invoiceDataSource
      .confirmAndSendToKsefById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private download(item: AdminInvoiceCollectionItem): void {
    this.invoiceDataSource.downloadOrderInvoiceById(item.id).subscribe();
  }

  private refresh(): void {
    this.table.refresh();
  }
}
